import {
  IMAGE_ASPECT_RATIO,
  IMAGE_QUALITY,
  CANVAS_SETTINGS,
  UPLOAD_SETTINGS,
  CROP_SETTINGS,
} from '../AgentHeadshotUploader.constants'

/**
 * Validates if a file is an image
 * @param {File} file - The file to validate
 * @returns {boolean} - Whether the file is a valid image
 */
export const isValidImageFile = file => {
  if (!file) return false
  if (!(file instanceof File || file instanceof Blob)) return false

  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  return validTypes.includes(file.type)
}

/**
 * Validates a data URL
 * @param {string} dataUrl - The data URL to validate
 * @returns {boolean} - Whether the data URL is valid
 */
export const isValidDataURL = dataUrl => {
  if (!dataUrl || typeof dataUrl !== 'string') return false
  return dataUrl.startsWith('data:image/')
}

/**
 * Reads a file and returns a data URL
 * @param {File} file - The file to read
 * @returns {Promise<string>} - Data URL of the file
 */
export const readFileAsDataURL = file => {
  return new Promise((resolve, reject) => {
    if (!isValidImageFile(file)) {
      reject(new Error('Invalid file type. Please select an image file (JPEG, PNG, GIF, or WebP).'))
      return
    }

    const reader = new FileReader()

    reader.addEventListener('load', () => {
      const result = reader.result
      if (!isValidDataURL(result)) {
        reject(new Error('Failed to read image file as data URL'))
        return
      }
      resolve(result)
    })

    reader.addEventListener('error', () => {
      reject(new Error('Failed to read file. The file may be corrupted or too large.'))
    })

    reader.readAsDataURL(file)
  })
}

/**
 * Loads an image from a data URL
 * @param {string} dataUrl - The data URL of the image
 * @returns {Promise<HTMLImageElement>} - Loaded image element
 */
export const loadImage = dataUrl => {
  return new Promise((resolve, reject) => {
    if (!isValidDataURL(dataUrl)) {
      reject(new Error('Invalid data URL format'))
      return
    }

    const img = new Image()

    img.onload = () => {
      // Validate that the image has valid dimensions
      if (img.width === 0 || img.height === 0) {
        reject(new Error('Image has invalid dimensions'))
        return
      }
      resolve(img)
    }

    img.onerror = () => {
      // Create a meaningful error instead of passing the Event object
      reject(new Error('Failed to load image. The file may be corrupted or in an unsupported format.'))
    }

    img.src = dataUrl
  })
}

/**
 * Processes an image to the target aspect ratio with padding
 * @param {string} imageDataUrl - The image data URL
 * @returns {Promise<string>} - Processed image data URL
 */
export const processImageToRatio = async imageDataUrl => {
  try {
    const img = await loadImage(imageDataUrl)

    // Validate image dimensions
    if (!img.width || !img.height || img.width === 0 || img.height === 0) {
      throw new Error('Image has invalid dimensions')
    }

    // Calculate target dimensions
    const minWidth = IMAGE_QUALITY.MIN_WIDTH
    const maxWidth = Math.max(img.width, IMAGE_QUALITY.MAX_WIDTH)
    const targetWidth = Math.max(
      minWidth,
      Math.min(maxWidth, img.width > IMAGE_QUALITY.OPTIMAL_WIDTH ? img.width : IMAGE_QUALITY.OPTIMAL_WIDTH)
    )
    const targetHeight = targetWidth / IMAGE_ASPECT_RATIO.RATIO

    // Validate calculated dimensions
    if (!isFinite(targetWidth) || !isFinite(targetHeight) || targetWidth <= 0 || targetHeight <= 0) {
      throw new Error('Calculated dimensions are invalid')
    }

    // Create canvas
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Failed to get canvas context')
    }

    // Enable high-quality image smoothing
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = CANVAS_SETTINGS.IMAGE_SMOOTHING_QUALITY

    // Fill background
    ctx.fillStyle = CANVAS_SETTINGS.BACKGROUND_COLOR
    ctx.fillRect(0, 0, targetWidth, targetHeight)

    // Add border
    const borderWidth = Math.max(1, targetWidth * CANVAS_SETTINGS.BORDER_RATIO)
    ctx.strokeStyle = CANVAS_SETTINGS.BORDER_COLOR
    ctx.lineWidth = borderWidth
    ctx.strokeRect(borderWidth / 2, borderWidth / 2, targetWidth - borderWidth, targetHeight - borderWidth)

    // Calculate image placement
    // Using Math.max scales to COVER (fill/zoom) - no blank space
    // Using Math.min scales to FIT (contain) - shows full image with padding
    const availableWidth = targetWidth * CANVAS_SETTINGS.PADDING_RATIO
    const availableHeight = targetHeight * CANVAS_SETTINGS.PADDING_RATIO

    const scaleToFitWidth = availableWidth / img.width
    const scaleToFitHeight = availableHeight / img.height
    const scale = Math.min(scaleToFitWidth, scaleToFitHeight) // Use Math.min to show complete image (contain behavior)

    const drawWidth = img.width * scale
    const drawHeight = img.height * scale
    const drawX = (targetWidth - drawWidth) / 2
    const drawY = (targetHeight - drawHeight) / 2

    // Draw the image centered and scaled to fit completely within canvas
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)

    // Convert to data URL with appropriate format and quality
    let dataUrl
    try {
      if (targetWidth > IMAGE_QUALITY.HIGH_RES_THRESHOLD || targetHeight > IMAGE_QUALITY.HIGH_RES_THRESHOLD) {
        dataUrl = canvas.toDataURL(UPLOAD_SETTINGS.PNG_MIME_TYPE)
      } else {
        dataUrl = canvas.toDataURL(UPLOAD_SETTINGS.MIME_TYPE, IMAGE_QUALITY.JPEG)
      }

      if (!dataUrl || !isValidDataURL(dataUrl)) {
        throw new Error('Failed to generate valid data URL from canvas')
      }

      return dataUrl
    } catch (err) {
      throw new Error(`Failed to convert canvas to data URL: ${err.message}`)
    }
  } catch (err) {
    // Re-throw with more context if it's not already an Error object
    if (err instanceof Error) {
      throw err
    }
    throw new Error('Failed to process image to ratio')
  }
}

/**
 * Crops an image based on crop parameters
 * @param {HTMLImageElement} image - The image element to crop
 * @param {Object} crop - Crop parameters (x, y, width, height)
 * @param {string} fileName - Name for the output file
 * @returns {Promise<Blob>} - Cropped image blob
 */
export const getCroppedImage = (image, crop, fileName = UPLOAD_SETTINGS.FILE_NAME) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    canvas.width = Math.ceil(crop.width * scaleX)
    canvas.height = Math.ceil(crop.height * scaleY)

    const ctx = canvas.getContext('2d')

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    )

    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('Canvas is empty'))
        return
      }
      blob.name = fileName
      resolve(blob)
    }, UPLOAD_SETTINGS.MIME_TYPE)
  })
}

/**
 * Calculates initial crop dimensions for an image
 * @param {HTMLImageElement} image - The image element
 * @returns {Object} - Initial crop configuration
 */
export const calculateInitialCrop = image => {
  // Work directly with the displayed image dimensions (no canvas processing)
  const imageWidth = image.width
  const imageHeight = image.height

  // Account for 2px CSS border on all sides (the image element has border: 2px solid #ddd)
  const borderWidth = 2
  const availableWidth = imageWidth - borderWidth * 2
  const availableHeight = imageHeight - borderWidth * 2

  // Calculate crop based on the actual image dimensions (no padding/processing)
  // Use INITIAL_WIDTH_PERCENT of the available image height
  const cropHeightPercent = CROP_SETTINGS.INITIAL_WIDTH_PERCENT / 100
  let cropHeight = availableHeight * cropHeightPercent
  let cropWidth = cropHeight * IMAGE_ASPECT_RATIO.RATIO

  // If calculated crop width exceeds available width, scale down to fit
  if (cropWidth > availableWidth) {
    cropWidth = availableWidth
    cropHeight = cropWidth / IMAGE_ASPECT_RATIO.RATIO
  }

  // Center the crop selector within the available image area
  // Add border offset to position correctly
  const cropX = borderWidth + (availableWidth - cropWidth) / 2
  const cropY = borderWidth + (availableHeight - cropHeight) / 2

  // console.log('Image dimensions:', imageWidth, 'x', imageHeight)
  // console.log('Available dimensions (minus border):', availableWidth, 'x', availableHeight)
  // console.log('Crop position:', cropX, ',', cropY)
  // console.log('Crop size:', cropWidth, 'x', cropHeight)

  return {
    unit: UPLOAD_SETTINGS.UNIT || 'px',
    width: cropWidth,
    height: cropHeight,
    x: cropX,
    y: cropY,
    aspect: IMAGE_ASPECT_RATIO.RATIO,
  }
}

/**
 * Creates FormData for headshot upload
 * @param {Blob} blob - The image blob
 * @param {string} fileName - The file name
 * @returns {FormData} - FormData ready for upload
 */
export const createHeadshotFormData = (blob, fileName = UPLOAD_SETTINGS.FILE_NAME) => {
  const formData = new FormData()
  formData.append(UPLOAD_SETTINGS.FORM_FIELD_NAME, blob)
  formData.append(UPLOAD_SETTINGS.FORM_FILENAME_FIELD, fileName)
  return formData
}
